import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { positionEventEmitter } from '../services/positionEventEmitter';
import { useUserDocument } from './UserDocumentContext';
import { useAuth } from './AuthContext';

export interface OpenPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  current: string;
  pnl: string;
  pnlPercent: string;
  leverage: number;
  timestamp: number;
  orderType: 'market' | 'limit' | 'stop';
  isAI?: boolean; // Flag to indicate if position was created by AI
}

export interface PendingOrder {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  price: string;
  stopPrice?: string;
  orderType: 'limit' | 'stop';
  status: 'Pending' | 'Filled' | 'Cancelled';
  leverage: number;
  timestamp: number;
}

export interface ClosedPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  close: string;
  pnl: string;
  pnlPercent: string;
  closeTime: string;
  status: 'Success' | 'Cancelled';
  timestamp: number;
  isAI?: boolean; // Flag to indicate if position was created by AI
}

interface PositionContextType {
  openPositions: OpenPosition[];
  pendingOrders: PendingOrder[];
  closedPositions: ClosedPosition[];
  closingPositions: Set<string>;
  totalUnrealizedPnL: number;
  addOpenPosition: (position: Omit<OpenPosition, 'id' | 'timestamp'>) => Promise<OpenPosition>;
  addPendingOrder: (order: Omit<PendingOrder, 'id' | 'timestamp'>) => void;
  closePosition: (positionId: string, closePrice: string) => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  editOrder: (orderId: string, newPrice: string, newSize: string) => void;
  fillOrder: (orderId: string, fillPrice: string) => void;
  updateCurrentPrices: (priceUpdates: { [symbol: string]: string }) => void;
}

const PositionContext = createContext<PositionContextType | undefined>(undefined);

export const usePositionContext = () => {
  const context = useContext(PositionContext);
  if (context === undefined) {
    throw new Error('usePositionContext must be used within a PositionProvider');
  }
  return context;
};

interface PositionProviderProps {
  children: ReactNode;
}

export const PositionProvider: React.FC<PositionProviderProps> = ({ children }) => {
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [closingPositions, setClosingPositions] = useState<Set<string>>(new Set());
  
  // Get live market data for real-time price updates
  const { getMarketDataForSymbol } = useBinanceMarketData();
  
  // Get account context for profit updates
  const { addProfit, forceFirebaseSync, openPosition: openPositionBalance, closePosition: closePositionBalance, updateSimplifiedEquity, setBalanceUpdateTrigger } = useAccount();
  
  // Get account type context
  const { accountType } = useAccountType();
  
  // Get auth context for user ID
  const { user } = useAuth();
  
  // Get user document context for persistence
  const { 
    userDocument, 
    updateOpenPosition, 
    removeOpenPosition, 
    addClosedTrade, 
    addTradingHistoryEntry 
  } = useUserDocument();

  // Ref to track previous values to prevent unnecessary emissions
  const prevValuesRef = useRef<{ totalUsedMargin: number; totalUnrealizedPnL: number; accountType: string } | null>(null);

  // Default thresholds (fallback values) - these should only be used if no Firestore data exists
  const DEFAULT_PROFIT_THRESHOLD = 1.0; // Generic fallback
  const DEFAULT_LOSS_THRESHOLD = 1.0;   // Generic fallback

  // Function to get thresholds from strategy configuration in user document
  const getThresholdsForSymbol = useCallback((symbol: string) => {
    try {
      if (userDocument && accountType) {
        // 🔥 CRITICAL FIX: Use correct data structure - demoAccountData/realAccountData
        const accountDataKey = accountType === 'live' ? 'realAccountData' : 'demoAccountData';
        const accountData = (userDocument as any)[accountDataKey];
        const aiTradingData = accountData?.aiTradingData;
        
        // console.log(`🔍 Searching for thresholds for ${symbol}:`, {
        //   hasUserDocument: !!userDocument,
        //   accountType,
        //   hasAccountData: !!accountData,
        //   hasAiTradingData: !!aiTradingData,
        //   activeStrategiesCount: aiTradingData?.activeStrategies?.length || 0,
        //   hasAssets: !!aiTradingData?.assets,
        //   assetKeys: aiTradingData?.assets ? Object.keys(aiTradingData.assets) : [],
        //   // 🔥 CRITICAL DEBUG: Check if our specific symbol exists in assets
        //   hasSymbolInAssets: !!aiTradingData?.assets?.[symbol],
        //   symbolAssetData: aiTradingData?.assets?.[symbol] ? {
        //     hasActiveStrategyInstance: !!aiTradingData.assets[symbol].activeStrategyInstance,
        //     hasConfig: !!aiTradingData.assets[symbol].activeStrategyInstance?.config,
        //     isActive: aiTradingData.assets[symbol].activeStrategyInstance?.isActive,
        //     aiTradeStartEnabled: aiTradingData.assets[symbol].activeStrategyInstance?.aiTradeStartEnabled
        //   } : null,
        //   // 🔥 CRITICAL DEBUG: Show the full aiTradingData structure
        //   fullAiTradingData: aiTradingData,
        //   // 🔥 CRITICAL DEBUG: Show specific parts of the data structure
        //   assetsStructure: aiTradingData?.assets,
        //   assetsKeys: aiTradingData?.assets ? Object.keys(aiTradingData.assets) : [],
        //   ethusdtAsset: aiTradingData?.assets?.ETHUSDT,
        //   ethusdtKeys: aiTradingData?.assets?.ETHUSDT ? Object.keys(aiTradingData.assets.ETHUSDT) : []
        // });
        
        // 🔥 CRITICAL DEBUG: Separate log for data structure analysis
        // console.log(`🔍 Data Structure Analysis for ${symbol}:`, {
        //   'accountDataKey': accountDataKey,
        //   'accountData exists': !!accountData,
        //   'aiTradingData exists': !!aiTradingData,
        //   'assets exists': !!aiTradingData?.assets,
        //   'assets type': typeof aiTradingData?.assets,
        //   'assets keys': aiTradingData?.assets ? Object.keys(aiTradingData.assets) : 'N/A',
        //   'ETHUSDT exists': !!aiTradingData?.assets?.ETHUSDT,
        //   'ETHUSDT keys': aiTradingData?.assets?.ETHUSDT ? Object.keys(aiTradingData.assets.ETHUSDT) : 'N/A',
        //   'activeStrategyInstance exists': !!aiTradingData?.assets?.ETHUSDT?.activeStrategyInstance,
        //   'config exists': !!aiTradingData?.assets?.ETHUSDT?.activeStrategyInstance?.config
        // });
        
        // 🔥 CRITICAL FIX: Check BOTH data structures for consistency
        
        // Method 1: Check per-asset structure (PRIMARY - this is where strategies are saved)
        if (aiTradingData?.assets?.[symbol]) {
          const assetData = aiTradingData.assets[symbol];
          // console.log(`🔍 Found asset data for ${symbol}:`, {
          //   hasActiveStrategyInstance: !!assetData.activeStrategyInstance,
          //   hasConfig: !!assetData.activeStrategyInstance?.config,
          //   assetKeys: Object.keys(assetData),
          //   fullAssetData: assetData
          // });
          
          if (assetData.activeStrategyInstance?.config) {
            const strategyInstance = assetData.activeStrategyInstance;
            const config = strategyInstance.config;
            
            // console.log(`🔍 Checking assets structure for ${symbol}:`, {
            //   hasActiveStrategyInstance: !!strategyInstance,
            //   isActive: strategyInstance.isActive,
            //   aiTradeStartEnabled: strategyInstance.aiTradeStartEnabled,
            //   hasConfig: !!config,
            //   configKeys: config ? Object.keys(config) : []
            // });
            
            // Check if strategy is active (either isActive or aiTradeStartEnabled)
            if (strategyInstance.isActive || strategyInstance.aiTradeStartEnabled) {
              const thresholds = {
                profitThreshold: config.profitThreshold || DEFAULT_PROFIT_THRESHOLD,
                lossThreshold: config.lossThreshold || DEFAULT_LOSS_THRESHOLD
              };
              
              // console.log(`✅ Retrieved thresholds for ${symbol} from assets structure:`, {
              //   thresholds,
              //   strategy: {
              //     id: strategyInstance.id,
              //     name: strategyInstance.name,
              //     isActive: strategyInstance.isActive,
              //     aiTradeStartEnabled: strategyInstance.aiTradeStartEnabled
              //   }
              // });
              return thresholds;
            } else {
              // console.log(`⚠️ Strategy for ${symbol} exists but is not active:`, {
              //   isActive: strategyInstance.isActive,
              //   aiTradeStartEnabled: strategyInstance.aiTradeStartEnabled
              // });
            }
          } else {
            // console.log(`⚠️ No activeStrategyInstance.config found for ${symbol}`);
          }
        } else {
          // console.log(`⚠️ No asset data found for ${symbol} in assets structure`);
        }
        
        // Method 2: Check activeStrategies array (fallback for legacy data)
        if (aiTradingData?.activeStrategies && Array.isArray(aiTradingData.activeStrategies)) {
          // console.log(`🔍 Checking activeStrategies for ${symbol}:`, aiTradingData.activeStrategies.map((s: any) => ({
          //   symbol: s.symbol,
          //   isActive: s.isActive,
          //   aiTradeStartEnabled: s.aiTradeStartEnabled,
          //   hasConfig: !!s.config,
          //   configKeys: s.config ? Object.keys(s.config) : []
          // })));
          
          const activeStrategy = aiTradingData.activeStrategies.find((strategy: any) => 
            strategy.symbol === symbol && (strategy.isActive || strategy.aiTradeStartEnabled)
          ) as any;
          
          if (activeStrategy?.config) {
            const thresholds = {
              profitThreshold: activeStrategy.config.profitThreshold || DEFAULT_PROFIT_THRESHOLD,
              lossThreshold: activeStrategy.config.lossThreshold || DEFAULT_LOSS_THRESHOLD
            };
            
            // console.log(`✅ Retrieved thresholds for ${symbol} from activeStrategies:`, {
            //   thresholds,
            //   strategy: {
            //     id: activeStrategy.id,
            //     name: activeStrategy.name,
            //     isActive: activeStrategy.isActive,
            //     aiTradeStartEnabled: activeStrategy.aiTradeStartEnabled
            //   }
            // });
            return thresholds;
          }
        }
      }
    } catch (error) {
      console.warn(`❌ Could not get thresholds for ${symbol}:`, error);
    }
    
    // Return default thresholds if strategy not found or no config
    // console.log(`⚠️ Using default thresholds for ${symbol} (no saved configuration found)`);
    return {
      profitThreshold: DEFAULT_PROFIT_THRESHOLD,
      lossThreshold: DEFAULT_LOSS_THRESHOLD
    };
  }, [userDocument, accountType]);

  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  // Load existing positions from Firestore when user document is available
  useEffect(() => {
    if (userDocument && accountType) {
      const accountData = userDocument.accounts?.[accountType];
      
      
      if (accountData) {
        // Convert Firestore positions to local format
        const firestorePositions: OpenPosition[] = accountData.openPositions.map(pos => {
          // Ensure openedAt is a Date object
          let openedAtDate: Date;
          
          if (pos.openedAt instanceof Date) {
            openedAtDate = pos.openedAt;
          } else if (pos.openedAt && typeof pos.openedAt === 'object' && 'seconds' in pos.openedAt) {
            // Handle Firestore Timestamp object
            const timestamp = pos.openedAt as { seconds: number; nanoseconds?: number };
            openedAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            openedAtDate = new Date(pos.openedAt);
          }
          
          return {
            id: pos.id,
            symbol: pos.symbol,
            type: pos.type === 'long' ? 'Buy' : 'Sell',
            size: pos.size.toString(),
            entry: pos.entryPrice.toString(),
            current: pos.currentPrice.toString(),
            pnl: pos.unrealizedPnL >= 0 ? `+$${pos.unrealizedPnL.toFixed(2)}` : `-$${Math.abs(pos.unrealizedPnL).toFixed(2)}`,
            pnlPercent: pos.unrealizedPnLPercent >= 0 ? `+${pos.unrealizedPnLPercent.toFixed(2)}%` : `-${Math.abs(pos.unrealizedPnLPercent).toFixed(2)}%`,
            leverage: pos.leverage,
            timestamp: openedAtDate.getTime(),
            orderType: 'market' as const,
            isAI: pos.source === 'ai'
          };
        });

        // Convert Firestore closed trades to local format
        const firestoreClosedPositions: ClosedPosition[] = accountData.closedTrades.map(trade => {
          // Ensure closedAt is a valid Date object
          let closedAtDate: Date;
          
          if (trade.closedAt instanceof Date) {
            closedAtDate = trade.closedAt;
          } else if (trade.closedAt && typeof trade.closedAt === 'object' && 'seconds' in trade.closedAt) {
            // Handle Firestore Timestamp object
            const timestamp = trade.closedAt as { seconds: number; nanoseconds?: number };
            closedAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            closedAtDate = new Date(trade.closedAt);
          }
          
          // Validate the date
          if (isNaN(closedAtDate.getTime())) {
            console.warn(`❌ Invalid closedAt date for trade ${trade.id}:`, trade.closedAt);
            closedAtDate = new Date(); // Fallback to current time
          }
          
          return {
            id: trade.id,
            symbol: trade.symbol,
            type: trade.type === 'long' ? 'Buy' : 'Sell',
            size: trade.size.toString(),
            entry: trade.entryPrice.toString(),
            close: trade.exitPrice.toString(),
            pnl: trade.realizedPnL >= 0 ? `+$${trade.realizedPnL.toFixed(2)}` : `-$${Math.abs(trade.realizedPnL).toFixed(2)}`,
            pnlPercent: trade.realizedPnLPercent >= 0 ? `+${trade.realizedPnLPercent.toFixed(2)}%` : `-${Math.abs(trade.realizedPnLPercent).toFixed(2)}%`,
            closeTime: closedAtDate.toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }),
            status: 'Success' as const,
            timestamp: closedAtDate.getTime(),
            isAI: trade.source === 'ai'
          };
        });

        // Convert Firestore open orders to local format
        const firestorePendingOrders: PendingOrder[] = (accountData.openOrders || []).map(order => {
          // Handle createdAt timestamp conversion
          let createdAtDate: Date;
          
          if (order.createdAt instanceof Date) {
            createdAtDate = order.createdAt;
          } else if (order.createdAt && typeof order.createdAt === 'object' && 'seconds' in order.createdAt) {
            // Handle Firestore Timestamp object
            const timestamp = order.createdAt as { seconds: number; nanoseconds?: number };
            createdAtDate = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
          } else {
            createdAtDate = new Date(order.createdAt);
          }
          
          return {
            id: order.id,
            symbol: order.symbol,
            type: order.type.includes('buy') ? 'Buy' : 'Sell',
            size: order.size.toString(),
            price: order.price.toString(),
            orderType: order.type.includes('limit') ? 'limit' : 'stop',
            status: 'Pending' as const,
            leverage: 100, // Default leverage since OpenOrder doesn't have leverage
            timestamp: createdAtDate.getTime(),
          };
        });

        setOpenPositions(firestorePositions);
        setClosedPositions(firestoreClosedPositions);
        setPendingOrders(firestorePendingOrders);
        
      }
    }
  }, [userDocument, accountType]);
  // Clear positions when account type changes (before loading new data)
  useEffect(() => {
    setOpenPositions([]);
    setClosedPositions([]);
    setPendingOrders([]);
  }, [accountType]);

  // Calculate total used margin from all open positions
  const calculateTotalUsedMargin = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const size = parseFloat(position.size);
      const leverage = position.leverage;
      const marginUsed = (entryPrice * size) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate margin from pending orders
  const calculatePendingOrdersMargin = useCallback((orders: PendingOrder[]): number => {
    return orders.reduce((total, order) => {
      const orderPrice = parseFloat(order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price);
      const orderSize = parseFloat(order.size);
      const leverage = order.leverage;
      const marginUsed = (orderPrice * orderSize) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate total unrealized P&L from all open positions
  const calculateTotalUnrealizedPnL = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const currentPrice = parseFloat(position.current);
      const size = parseFloat(position.size);
      
      let pnl: number;
      if (position.type === 'Buy') {
        pnl = (currentPrice - entryPrice) * size;
      } else {
        pnl = (entryPrice - currentPrice) * size;
      }
      
      return total + pnl;
    }, 0);
  }, []);

  // Function to calculate P&L for a position
  const calculatePnL = useCallback((entryPrice: number, currentPrice: number, size: number, type: 'Buy' | 'Sell') => {
    let pnl: number;
    if (type === 'Buy') {
      pnl = (currentPrice - entryPrice) * size;
    } else {
      pnl = (entryPrice - currentPrice) * size;
    }
    const pnlPercent = ((pnl / (entryPrice * size)) * 100).toFixed(2);
    const pnlFormatted = pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
    const pnlPercentFormatted = pnl >= 0 ? `+${pnlPercent}%` : `-${Math.abs(parseFloat(pnlPercent))}%`;
    
    return { pnl: pnlFormatted, pnlPercent: pnlPercentFormatted };
  }, []);

  const addOpenPosition = async (positionData: Omit<OpenPosition, 'id' | 'timestamp'>) => {
    // Calculate initial P&L
    const entryPrice = parseFloat(positionData.entry);
    const currentPrice = parseFloat(positionData.current);
    const size = parseFloat(positionData.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, positionData.type);
    
    const newPosition: OpenPosition = {
      ...positionData,
      id: generateId(),
      timestamp: Date.now(),
      pnl,
      pnlPercent,
      orderType: positionData.orderType || 'market', // Default to market if not specified
    };
    
    // Log trade execution
    if (positionData.isAI) {
    } else {
    }
    
    // Save to Firestore
    try {
      const firestorePosition = {
        id: newPosition.id,
        symbol: newPosition.symbol,
        type: newPosition.type === 'Buy' ? 'long' as const : 'short' as const,
        size: parseFloat(newPosition.size),
        entryPrice: parseFloat(newPosition.entry),
        currentPrice: parseFloat(newPosition.current),
        stopLoss: null, // Use null instead of undefined
        takeProfit: null, // Use null instead of undefined
        openedAt: new Date(newPosition.timestamp),
        lastUpdated: new Date(),
        unrealizedPnL: parseFloat(pnl.replace(/[+$,]/g, '')),
        unrealizedPnLPercent: parseFloat(pnlPercent.replace(/[+%,]/g, '')),
        marginUsed: (parseFloat(newPosition.entry) * parseFloat(newPosition.size)) / newPosition.leverage,
        leverage: newPosition.leverage,
        swap: 0,
        commission: 0,
        comment: positionData.isAI ? 'AI Trade' : 'Manual Trade',
        source: positionData.isAI ? 'ai' as const : 'manual' as const
      };
      
      await updateOpenPosition(accountType, firestorePosition);
      
      // Update simplified balance system
      const marginRequired = (parseFloat(newPosition.entry) * parseFloat(newPosition.size)) / newPosition.leverage;
      await openPositionBalance(accountType, marginRequired, newPosition.id, newPosition.symbol);
      
      // Trigger balance update
      setBalanceUpdateTrigger(prev => prev + 1);
      
      // Add trading history entry
      await addTradingHistoryEntry(accountType, {
        id: `hist_${newPosition.id}`,
        type: 'trade',
        symbol: newPosition.symbol,
        amount: parseFloat(pnl.replace(/[+$,]/g, '')),
        price: parseFloat(newPosition.entry),
        quantity: parseFloat(newPosition.size),
        timestamp: new Date(newPosition.timestamp),
        status: 'completed',
        description: `${positionData.isAI ? 'AI' : 'Manual'} ${newPosition.type} trade executed`,
        source: positionData.isAI ? 'ai' : 'manual',
        reference: newPosition.id
      });
      
    } catch (error) {
      console.error('Error saving position to Firestore:', error);
    }
    
    setOpenPositions(prev => {
      const updatedPositions = [newPosition, ...prev];
      
      // Emit position update after state change
      const timeoutId = setTimeout(() => {
        const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
        const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
        positionEventEmitter.emit('positionsUpdated', { 
          totalUsedMargin, 
          totalUnrealizedPnL, 
          accountType 
        });
      }, 0);
      
      // Store timeout ID for potential cleanup
      return updatedPositions;
    });
    
    return newPosition; // Return the position so AI context can track it
  };

  const addPendingOrder = (orderData: Omit<PendingOrder, 'id' | 'timestamp'>) => {
    const newOrder: PendingOrder = {
      ...orderData,
      id: generateId(),
      timestamp: Date.now(),
    };
    setPendingOrders(prev => [newOrder, ...prev]);
  };

  const closePosition = async (positionId: string, closePrice: string) => {
    const position = openPositions.find(p => p.id === positionId);
    if (!position) return;

    // Check if position is already being closed to prevent multiple closes
    if (closingPositions.has(positionId)) {
      console.warn(`Position ${positionId} is already being closed`);
      return;
    }

    // Mark position as being closed
    setClosingPositions(prev => new Set(prev).add(positionId));

    try {
      // Calculate P&L using the helper function
      const entryPrice = parseFloat(position.entry);
      const closePriceNum = parseFloat(closePrice);
      const size = parseFloat(position.size);
      const { pnl, pnlPercent } = calculatePnL(entryPrice, closePriceNum, size, position.type);

      // Calculate numeric P&L for profit addition
      let numericPnL: number;
      if (position.type === 'Buy') {
        numericPnL = (closePriceNum - entryPrice) * size;
      } else {
        numericPnL = (entryPrice - closePriceNum) * size;
      }

      // Calculate margin that was used for this position (to return it to balance)
      const leverage = position.leverage;
      const marginUsed = (entryPrice * size) / leverage;

      // Update simplified balance system (margin release + P&L)
      const description = `${position.symbol} ${position.type} position closed at ${closePrice}`;
      await closePositionBalance(accountType, marginUsed, numericPnL, position.id, position.symbol);
      
      // Also update legacy system for compatibility
      await addProfit(numericPnL, marginUsed, description, accountType);
      
      // Trigger balance update
      setBalanceUpdateTrigger(prev => prev + 1);
      
      // Force Firebase sync after position closing to ensure balance consistency
      setTimeout(() => {
        forceFirebaseSync();
      }, 3000); // 3 second delay to allow addProfit and Firebase update to complete

      // Add to closed positions
      const closedPosition: ClosedPosition = {
        id: position.id,
        symbol: position.symbol,
        type: position.type,
        size: position.size,
        entry: position.entry,
        close: closePrice,
        pnl,
        pnlPercent,
        closeTime: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }),
        status: 'Success',
        timestamp: Date.now(),
        isAI: position.isAI, // Preserve AI flag
      };

      // Save closed trade to Firestore
      try {
        // Ensure position.timestamp is a valid number
        const openedAtTimestamp = typeof position.timestamp === 'number' && !isNaN(position.timestamp) 
          ? position.timestamp 
          : Date.now() - (5 * 60 * 1000); // Default to 5 minutes ago if invalid

        // Create valid Date objects
        const openedAtDate = new Date(openedAtTimestamp);
        const closedAtDate = new Date();

        // Validate dates before proceeding
        if (isNaN(openedAtDate.getTime())) {
          console.error('❌ Invalid openedAt date for position:', position.id, 'timestamp:', position.timestamp);
          throw new Error('Invalid position timestamp');
        }
        
        if (isNaN(closedAtDate.getTime())) {
          console.error('❌ Invalid closedAt date for position:', position.id);
          throw new Error('Invalid close date');
        }

        const firestoreClosedTrade = {
          id: position.id,
          symbol: position.symbol,
          type: position.type === 'Buy' ? 'long' as const : 'short' as const,
          size: parseFloat(position.size),
          entryPrice: parseFloat(position.entry),
          exitPrice: parseFloat(closePrice),
          stopLoss: null, // Use null instead of undefined
          takeProfit: null, // Use null instead of undefined
          openedAt: openedAtDate,
          closedAt: closedAtDate,
          duration: Math.floor((closedAtDate.getTime() - openedAtDate.getTime()) / (1000 * 60)), // duration in minutes
          realizedPnL: numericPnL,
          realizedPnLPercent: parseFloat(pnlPercent.replace(/[+%,]/g, '')),
          swap: 0,
          commission: 0,
          totalPnL: numericPnL,
          comment: position.isAI ? 'AI Trade' : 'Manual Trade',
          source: position.isAI ? 'ai' as const : 'manual' as const,
          closeReason: 'manual' as const
        };

        
        await addClosedTrade(accountType, firestoreClosedTrade);
        
        // Remove from open positions in Firestore
        await removeOpenPosition(accountType, positionId);
        
        // Add trading history entry
        await addTradingHistoryEntry(accountType, {
          id: `hist_close_${position.id}`,
          type: 'trade',
          symbol: position.symbol,
          amount: numericPnL,
          price: parseFloat(closePrice),
          quantity: parseFloat(position.size),
          timestamp: new Date(),
          status: 'completed',
          description: `${position.isAI ? 'AI' : 'Manual'} ${position.type} trade closed`,
          source: position.isAI ? 'ai' : 'manual',
          reference: position.id
        });
        
      } catch (error) {
        console.error('Error saving closed trade to Firestore:', error);
      }

      setClosedPositions(prev => [closedPosition, ...prev]);
      setOpenPositions(prev => {
        const updatedPositions = prev.filter(p => p.id !== positionId);
        
        // Immediately emit position update to ensure margin is returned instantly
        // This ensures the margin is immediately returned to balance when position closes
        setTimeout(() => {
          const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
          const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
          positionEventEmitter.emit('positionsUpdated', { 
            totalUsedMargin, 
            totalUnrealizedPnL, 
            accountType 
          });
        }, 0);
        
        return updatedPositions;
      });

    } finally {
      // Always remove position from closingPositions Set, even if there was an error
      setClosingPositions(prev => {
        const newSet = new Set(prev);
        newSet.delete(positionId);
        return newSet;
      });
    }
  };

  const cancelOrder = async (orderId: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // Calculate margin that was reserved for this order (to return it to balance)
    const marginUsed = (parseFloat(order.price) * parseFloat(order.size)) / order.leverage;

    // Return the margin to the account balance (no P&L, just margin return)
    if (addProfit) {
      await addProfit(0, marginUsed, `Margin returned for cancelled ${order.symbol} order`, accountType);
    }

    // Add to closed positions as cancelled
    const cancelledOrder: ClosedPosition = {
      id: order.id,
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: order.price,
      close: order.price,
      pnl: '$0.00',
      pnlPercent: '0.00%',
      closeTime: new Date().toLocaleString(),
      status: 'Cancelled',
      timestamp: Date.now(),
    };

    setClosedPositions(prev => [cancelledOrder, ...prev]);
    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
  };

  const editOrder = (orderId: string, newPrice: string, newSize: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    const fieldToUpdate = order?.orderType === 'stop' ? 'stopPrice' : 'price';
    
    setPendingOrders(prev => 
      prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              // For stop orders, update the stopPrice (trigger price)
              // For limit orders, update the price (execution price)
              ...(order.orderType === 'stop' 
                ? { stopPrice: newPrice } 
                : { price: newPrice }
              ),
              size: newSize 
            }
          : order
      )
    );
  };

  const fillOrder = useCallback((orderId: string, fillPrice: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // Calculate initial P&L (should be $0.00 since entry = current)
    const entryPrice = parseFloat(fillPrice);
    const currentPrice = parseFloat(fillPrice);
    const size = parseFloat(order.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, order.type);

    // Move to open positions
    const newPosition: OpenPosition = {
      id: order.id,
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: fillPrice,
      current: fillPrice,
      pnl,
      pnlPercent,
      leverage: order.leverage, // Use the leverage from the original order
      timestamp: Date.now(),
      orderType: order.orderType, // Use the order type from the original order
    };

    setOpenPositions(prev => [newPosition, ...prev]);
    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
  }, [pendingOrders, calculatePnL]);

  const updateCurrentPrices = useCallback((priceUpdates: { [symbol: string]: string }) => {
    setOpenPositions(prev => {
      const updatedPositions = prev.map(position => {
        const newPrice = priceUpdates[position.symbol];
        if (!newPrice) return position;

        // Only update if the price actually changed
        if (position.current === newPrice) {
          return position;
        }

        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(newPrice);
        const size = parseFloat(position.size);
        const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, position.type);

        return {
          ...position,
          current: newPrice,
          pnl,
          pnlPercent,
        };
      });
      
      // Only return new array if something actually changed
      const hasChanges = updatedPositions.some((pos, index) => 
        pos.current !== prev[index].current || 
        pos.pnl !== prev[index].pnl || 
        pos.pnlPercent !== prev[index].pnlPercent
      );
      
      return hasChanges ? updatedPositions : prev;
    });
  }, [calculatePnL]);

  // Emit position updates after state changes to avoid React warnings
  useEffect(() => {
    const positionsMargin = calculateTotalUsedMargin(openPositions);
    const pendingOrdersMargin = calculatePendingOrdersMargin(pendingOrders);
    const totalUsedMargin = positionsMargin + pendingOrdersMargin;
    const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);
    
    // Check if values have actually changed to prevent infinite loops
    const currentValues = { totalUsedMargin, totalUnrealizedPnL, accountType };
    const prevValues = prevValuesRef.current;
    
    if (!prevValues || 
        prevValues.totalUsedMargin !== totalUsedMargin || 
        prevValues.totalUnrealizedPnL !== totalUnrealizedPnL || 
        prevValues.accountType !== accountType) {
      
      // Update the ref with current values
      prevValuesRef.current = currentValues;
      
      // Debug logging
      
      // Emit updates for both positions and pending orders
      positionEventEmitter.emit('positionsUpdated', currentValues);
      
      // Update equity with current unrealized P&L (only if user is authenticated)
      if (updateSimplifiedEquity && user) {
        updateSimplifiedEquity(accountType, totalUnrealizedPnL).catch(error => {
          console.error('Error updating equity:', error);
        });
      }
    }
  }, [openPositions, pendingOrders, accountType, calculateTotalUsedMargin, calculatePendingOrdersMargin, calculateTotalUnrealizedPnL, updateSimplifiedEquity, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (prevValuesRef.current) {
        prevValuesRef.current = null;
      }
    };
  }, []);

  // Function to check pending orders for execution
  const checkPendingOrdersForExecution = useCallback(() => {
    if (pendingOrders.length === 0) return;

    
    pendingOrders.forEach(order => {
      const marketData = getMarketDataForSymbol(order.symbol);
      if (marketData) {
        const currentPrice = parseFloat(marketData.price);
        const orderPrice = parseFloat(order.price);
        const stopPrice = order.stopPrice ? parseFloat(order.stopPrice) : null;
        
        // Order execution logic
        
        let shouldExecute = false;
        let executionReason = '';
        
        if (order.orderType === 'limit') {
          // Limit order logic
          if (order.type === 'Buy') {
            // Buy limit: execute when current price <= limit price
            shouldExecute = currentPrice <= orderPrice;
            executionReason = `Buy limit: ${currentPrice} <= ${orderPrice}? ${shouldExecute}`;
          } else {
            // Sell limit: execute when current price >= limit price
            shouldExecute = currentPrice >= orderPrice;
            executionReason = `Sell limit: ${currentPrice} >= ${orderPrice}? ${shouldExecute}`;
          }
        } else if (order.orderType === 'stop') {
          // Stop order logic
          if (order.type === 'Buy') {
            // Buy stop: execute when current price >= stop price
            shouldExecute = stopPrice ? currentPrice >= stopPrice : false;
            executionReason = `Buy stop: ${currentPrice} >= ${stopPrice}? ${shouldExecute}`;
          } else {
            // Sell stop: execute when current price <= stop price
            shouldExecute = stopPrice ? currentPrice <= stopPrice : false;
            executionReason = `Sell stop: ${currentPrice} <= ${stopPrice}? ${shouldExecute}`;
          }
        }
        
        if (shouldExecute) {
          fillOrder(order.id, marketData.price);
        }
      } else {
      }
    });
  }, [pendingOrders, getMarketDataForSymbol, fillOrder]);

  // Automatically update current prices and P&L when positions change
  useEffect(() => {
    if (openPositions.length === 0 && pendingOrders.length === 0) return;

    // Create price updates object from market data
    const priceUpdates: { [symbol: string]: string } = {};
    
    // Update open positions
    openPositions.forEach(position => {
      const marketData = getMarketDataForSymbol(position.symbol);
      if (marketData && marketData.price !== position.current) {
        priceUpdates[position.symbol] = marketData.price;
      }
    });

    // Check pending orders for execution immediately when dependencies change
    checkPendingOrdersForExecution();

    // Only update if we have price data that's different from current prices
    if (Object.keys(priceUpdates).length > 0) {
      updateCurrentPrices(priceUpdates);
    }
  }, [openPositions, pendingOrders, getMarketDataForSymbol, checkPendingOrdersForExecution]);

  // Periodic check for pending order execution - this ensures orders execute when market price changes
  useEffect(() => {
    if (pendingOrders.length === 0) return;

    const interval = setInterval(() => {
      
      checkPendingOrdersForExecution();
    }, 1000); // Check every second

    return () => {
      clearInterval(interval);
    };
  }, [pendingOrders.length, checkPendingOrdersForExecution]);

  // Calculate total unrealized P&L
  const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);

  // Monitor all positions (AI and manual) for auto-close when profit >= PROFIT_THRESHOLD or loss >= LOSS_THRESHOLD
  useEffect(() => {
    const checkAllPositionsForAutoClose = () => {
      // Get all positions (both AI and manual) that are not currently being closed
      const allPositions = openPositions.filter(position => !closingPositions.has(position.id));
      
      // Debug logging
      if (openPositions.length > 0) {
      }
      
      // Sort positions by profit (highest profit first for priority closing)
      const sortedPositions = allPositions.sort((a, b) => {
        const aPnL = a.type === 'Buy' 
          ? (parseFloat(a.current) - parseFloat(a.entry)) * parseFloat(a.size)
          : (parseFloat(a.entry) - parseFloat(a.current)) * parseFloat(a.size);
        const bPnL = b.type === 'Buy' 
          ? (parseFloat(b.current) - parseFloat(b.entry)) * parseFloat(b.size)
          : (parseFloat(b.entry) - parseFloat(b.current)) * parseFloat(b.size);
        return bPnL - aPnL; // Highest profit first
      });
      
      sortedPositions.forEach(position => {
        // Skip if position doesn't have valid current price
        if (!position.current || position.current === '0' || position.current === '') {
          return;
        }

        // Calculate current P&L
        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(position.current);
        const size = parseFloat(position.size);
        
        // Skip if any values are invalid
        if (isNaN(entryPrice) || isNaN(currentPrice) || isNaN(size)) {
          return;
        }
        
        let currentPnL: number;
        if (position.type === 'Buy') {
          currentPnL = (currentPrice - entryPrice) * size;
        } else {
          currentPnL = (entryPrice - currentPrice) * size;
        }
        
        // Debug logging for all positions
        const positionType = position.isAI ? 'AI' : 'Manual';
        
        // Auto-close if profit >= PROFIT_THRESHOLD OR loss >= LOSS_THRESHOLD
        // BUT ONLY for AI trades - manual trades should be closed manually by user
        if (position.isAI) {
          // Get thresholds from strategy configuration for this symbol
          const thresholds = getThresholdsForSymbol(position.symbol);
          const { profitThreshold, lossThreshold } = thresholds;
          
          if (currentPnL >= profitThreshold || currentPnL <= -lossThreshold) {
            const profitOrLoss = currentPnL >= 0 ? 'profit' : 'loss';
            const threshold = currentPnL >= 0 ? profitThreshold : lossThreshold;
            
            // console.log(`🎯 AI Auto-Close: ${position.symbol} ${positionType} position hit ${profitOrLoss} threshold: $${currentPnL.toFixed(2)} >= $${threshold}`);
            
            // Close the position
            closePosition(position.id, position.current);
            
            // Additional logging for high profit positions
            if (currentPnL >= 1.00) {
              // console.log(`💰 High profit AI position closed: ${position.symbol} with $${currentPnL.toFixed(2)} profit`);
            }
          }
        }
      });
    };

    // Check every 500ms for all positions to auto-close (faster response)
    const interval = setInterval(checkAllPositionsForAutoClose, 500);

    return () => {
      clearInterval(interval);
    };
  }, [openPositions, closingPositions, closePosition, getThresholdsForSymbol]);

  const value: PositionContextType = {
    openPositions,
    pendingOrders,
    closedPositions,
    closingPositions,
    totalUnrealizedPnL,
    addOpenPosition,
    addPendingOrder,
    closePosition,
    cancelOrder,
    editOrder,
    fillOrder,
    updateCurrentPrices,
  };

  return (
    <PositionContext.Provider value={value}>
      {children}
    </PositionContext.Provider>
  );
};
